import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { toastError } from '@src/helpers/toastError'
import { PayloadCreatorParams } from '@src/redux/types'
import { omit } from 'ramda'

import * as api from '../api'
import {
  INQUIRY_ALL,
  INQUIRY_ASSIGNED,
  INQUIRY_COMMENTS,
  INQUIRY_COMMENT_CREATE,
  INQUIRY_COMMENT_DELETE,
  INQUIRY_CREATE,
  INQUIRY_DELETE,
  INQUIRY_ITEM,
  INQUIRY_LIST,
  INQUIRY_PUBLIC_INFO,
  INQUIRY_UPDATE_STATUS,
} from '../constants/actionTypes'
import * as API from '../constants/api'

export const inquiryAll = createAsyncThunk(
  INQUIRY_ALL,
  async (params: PayloadCreatorParams<`${typeof API.INQUIRY_ALL}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.inquiryAll(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const inquiryCreate = createAsyncThunk(
  INQUIRY_CREATE,
  async (params: PayloadCreatorParams<`${typeof API.INQUIRY_CREATE}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.inquiryCreate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const inquiryList = createAsyncThunk(
  INQUIRY_LIST,
  async (params: PayloadCreatorParams<`${typeof API.INQUIRY}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.inquiryList(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const inquiryAssigned = createAsyncThunk(
  INQUIRY_ASSIGNED,
  async (params: PayloadCreatorParams<`${typeof API.INQUIRY_ASSIGNED}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.inquiryAssigned(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const inquiryItemDelete = createAsyncThunk(
  INQUIRY_DELETE,
  async (params: PayloadCreatorParams<`${typeof API.INQUIRY_DELETE}{id}`, 'delete'>, { rejectWithValue }) => {
    try {
      const response = await api.inquiryItemDelete(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const inquiryItem = createAsyncThunk(
  INQUIRY_ITEM,
  async (params: PayloadCreatorParams<`${typeof API.INQUIRY}{id}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.inquiryItem(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const inquiryUpdateStatus = createAsyncThunk(
  INQUIRY_UPDATE_STATUS,
  async (params: PayloadCreatorParams<`${typeof API.INQUIRY_UPDATE_STATUS}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.inquiryUpdateStatus(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const inquiryComments = createAsyncThunk(
  INQUIRY_COMMENTS,
  async (params: PayloadCreatorParams<`${typeof API.INQUIRY_COMMENT}`, 'get'> | undefined, { rejectWithValue }) => {
    try {
      const response = await api.inquiryComments()
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const inquiryCommentCreate = createAsyncThunk(
  INQUIRY_COMMENT_CREATE,
  async (params: PayloadCreatorParams<`${typeof API.INQUIRY_COMMENT}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.inquiryCommentCreate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const inquiryCommentDelete = createAsyncThunk(
  INQUIRY_COMMENT_DELETE,
  async (params: PayloadCreatorParams<`${typeof API.INQUIRY_COMMENT}{id}`, 'delete'>, { rejectWithValue }) => {
    try {
      const response = await api.inquiryCommentDelete(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const inquiryPublicInfo = createAsyncThunk(
  INQUIRY_PUBLIC_INFO,
  async (params: PayloadCreatorParams<`${typeof API.INQUIRY_PUBLIC_INFO}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.inquiryPublicInfo(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const inquiryAllClear = createAction(`${INQUIRY_ALL}/clear`)

export const inquiryAssignedClear = createAction(`${INQUIRY_ASSIGNED}/clear`)

export const inquiryItemClear = createAction(`${INQUIRY_ITEM}/clear`)

export const inquiryListClear = createAction(`${INQUIRY_LIST}/clear`)

export const inquiryCommentsClear = createAction(`${INQUIRY_COMMENTS}/clear`)

export const inquiryPublicInfoClear = createAction(`${INQUIRY_PUBLIC_INFO}/clear`)
